/**
 * WHAT THIS PERSON MAY SEE, ASKED OF THE DATABASE.
 *
 * There is no card list in this file, and there must never be one. The cards
 * live in `admin_cards`, the mapping from card to permission lives in
 * `admin_card_permissions`, and fn_my_admin_cards() applies the filter server
 * side. A hardcoded array here would be a second copy of the taxonomy of the
 * admin surface, and the consumer-facing site has already been bitten once by
 * exactly that: a form carried nineteen hardcoded trades against a table of
 * twenty-eight live categories, under slugs nothing else on the platform used.
 *
 * The rule this follows: the client renders what the server returns, and asks
 * the server what to render.
 */

import { supabase, isSupabaseConfigured } from './supabaseClient';
import { refreshMyMfaState } from './mfa';

const fail = (error) => ({ data: null, error });

/**
 * The permission-filtered menu. Canon 1.1 — one login, one screen, the cards
 * you qualify for. The Master Dashboard arrives in this same list, flagged
 * is_master, for whoever qualifies; it is not a separate call, because it is
 * not a separate login.
 */
export async function fetchMyCards() {
  if (!isSupabaseConfigured()) {
    return fail('This portal was built without its Supabase credentials, so it cannot reach the database.');
  }
  const { data, error } = await supabase.rpc('fn_my_admin_cards');
  if (error) return fail(error.message || 'The portal menu could not be loaded.');
  return { data: data || [], error: null };
}

/**
 * What is actually behind a card, for this person, with the safeguards
 * attached.
 *
 * The safeguards come from the database rather than from a constant here for
 * the same reason as the cards: requires_reason_capture is a property of the
 * permission, and a screen that remembers it separately is a screen that
 * forgets it separately.
 */
export async function fetchCardPermissions(cardSlug) {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  if (!cardSlug) return { data: [], error: null };
  const { data, error } = await supabase.rpc('fn_my_card_permissions', { p_card_slug: cardSlug });
  if (error) return fail(error.message || 'That module could not be opened.');
  return { data: data || [], error: null };
}

/**
 * Everything this account holds, across every card and none.
 *
 *   "let it stand by itself outside all the cards, at the very top of the
 *    screen."
 *
 * The home screen needs this because a capability that stands outside a card
 * cannot be found by asking a card. The alternative was calling
 * fetchCardPermissions('human_capital') from Portal.jsx, which writes a card
 * slug into a React file to decide something that is no longer about that card
 * — so the day field onboarding moves, the home screen keeps asking Human
 * Capital and quietly stops rendering for the people who need it.
 *
 * Script 162. There is no argument that could make it answer about anybody but
 * the caller, which is the difference between it and admin_has_permission.
 *
 * A DATABASE WITHOUT SCRIPT 162 GETS AN EMPTY LIST, NOT AN ERROR BANNER. The
 * standing panel simply does not draw, the cards below it are untouched, and
 * nobody is shown a fault for a migration that has not run yet.
 */
export async function fetchMyPermissions() {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const { data, error } = await supabase.rpc('fn_my_permissions');
  if (error) {
    if (error.code === 'PGRST202') return { data: [], error: null };
    return fail(error.message || 'Your permissions could not be read.');
  }
  return { data: data || [], error: null };
}

/**
 * Who is signed in, as staff.
 *
 * Returns null for a valid session belonging to somebody who is not staff —
 * which is a real case, not an error: the same human may hold a consumer
 * account, and canon 1.2 says that account grants nothing here.
 */
export async function fetchMyStaffAccount() {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const uid = (await supabase.auth.getUser()).data?.user?.id || '';
  const { data, error } = await supabase
    .from('admin_users')
    .select('id, full_name, email, role, status, mfa_enrolled, last_login_at, oid_admin_id')
    .eq('auth_user_id', uid)
    .maybeSingle();

  if (error) return fail(error.message || 'Your staff account could not be read.');
  if (!data) return { data: null, error: null };

  // ASKED SEPARATELY, ON PURPOSE.
  //
  // id_number_hash arrives with script 95, and PostgREST fails the WHOLE select
  // on one unknown column — so naming it above would have turned a portal
  // deployed an hour ahead of its database into a portal nobody could sign in
  // to at all. This asks on its own and treats a failure as "not known yet",
  // which is the truth in both cases.
  //
  // WHETHER, NOT WHAT. The hash answers one question — is this person's OID
  // tied to them, or only to this record — and is dropped the moment it has.
  // Nothing on any screen needs the value.
  let hasIdOnFile;
  const anchored = await supabase
    .from('admin_users')
    .select('id_number_hash')
    .eq('auth_user_id', uid)
    .maybeSingle();
  if (!anchored.error && anchored.data) hasIdOnFile = Boolean(anchored.data.id_number_hash);

  // AND THE ENROLMENT IS DERIVED, NOT READ OFF THE ROW.
  //
  // THE BUG THE FOUNDER SAW. The directory said "No authenticator" against the
  // founding seat while nothing anywhere offered to set one up — two screens
  // reading the same question from two different places and disagreeing.
  //
  // admin_users.mfa_enrolled is a CACHE. Script 98 repointed the two grant
  // triggers, script 49's own guard and fn_staff_directory at the credential in
  // auth.mfa_factors, and left this one call — the one the person themselves
  // loads — reading the stored column. The founding seat carries true there,
  // because an account holding 'all' could not have been created without it,
  // so the enrolment panel was hidden from the one person most needing it.
  //
  // fn_refresh_my_mfa_state does both halves: it reads the authenticator and
  // writes the corrected value back, so the stale true is repaired once and
  // stays repaired. Script 98's own comment already said this happened on every
  // portal load. Nothing called it.
  //
  // A failure here is not "not enrolled". The cached value is the only other
  // thing there is to go on, and it is what a database without script 98 would
  // have anyway.
  const derived = await refreshMyMfaState();
  const mfa = derived.error ? data.mfa_enrolled : Boolean(derived.data?.mfa_enrolled);

  return { data: { ...data, mfa_enrolled: mfa, has_id_on_file: hasIdOnFile }, error: null };
}

/**
 * A staff seat waiting to be claimed by whoever is signed in.
 *
 *   "I added the user in Supabase with the gali email and a unique password,
 *    but when I use those credentials on the admin site, it says it's not a
 *    staff account."
 *
 * Creating a user in Supabase Auth creates an auth.users row and nothing else.
 * This portal has never looked at auth.users — it looks at admin_users, matched
 * on auth_user_id — so a login with no staff row is correctly refused. Canon
 * 1.2: staff identity is a separate system from platform identity.
 *
 * The missing half was the JOIN. fn_invite_staff_member writes an admin_users
 * row with auth_user_id NULL and status 'pending'; fn_claim_my_staff_seat
 * attaches a confirmed login to it. Both shipped. Nothing in this portal called
 * either of them, so an invited person signed in and was told they were not
 * staff — with a Sign out button and no way forward.
 *
 * Matched on the CONFIRMED address, never on a typed one: the invitation is the
 * address, exactly as it is for a technician on the consumer side.
 */
export async function fetchMyStaffInvite() {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const { data, error } = await supabase.rpc('fn_my_staff_invite');
  if (error) {
    // A database without the function is a deployment that is behind, and
    // saying so beats reporting "no invitation" for something never asked.
    if (error.code === 'PGRST202') {
      return fail('This portal needs a database update that has not been applied yet.');
    }
    return fail(error.message || 'Your invitation could not be read.');
  }
  return { data: (Array.isArray(data) ? data[0] : data) || null, error: null };
}

/**
 * Attach this login to the seat somebody invited, and tie the OID to the
 * person rather than to the record.
 *
 *   "yes, make staff OIDs permanent like OFX Codes, so that if the same staff
 *    member comes back years later, we can recognise them by the id number
 *    hash and link their oid."
 *
 * The ID number is hashed inside the database and never stored. If Orchid has
 * seen that person before — a previous employment, an invitation withdrawn and
 * re-issued — this returns the OID they have always had rather than a new one.
 *
 * Optional to the database and required by the screen. A claim without one
 * still opens the seat, deliberately: a database updated ahead of a portal
 * deploy must not lock somebody out of a door that exists.
 */
export async function claimMyStaffSeat(idNumber) {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const trimmed = (idNumber || '').trim();
  const { data, error } = await supabase.rpc(
    'fn_claim_my_staff_seat',
    trimmed ? { p_id_number: trimmed } : {},
  );
  if (error) {
    // Every one of these has a different fix, and only one of them is "try
    // again", so the database's own words are kept rather than flattened.
    if (error.code === 'PGRST202') {
      return fail('This portal needs a database update that has not been applied yet.');
    }
    return fail(error.message || 'That seat could not be claimed.');
  }
  return { data: (Array.isArray(data) ? data[0] : data) || null, error: null };
}

/**
 * For somebody who is already staff and predates the registry.
 *
 * Every staff member who claimed a seat before script 95 has an OID that is a
 * property of a database row and nothing else: delete the row and the code
 * refers to nobody. This is how they tie it to themselves, once, from inside
 * the portal — without it the rule would only ever apply to people hired after
 * the day it shipped.
 */
export async function recordMyStaffIdNumber(idNumber) {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const trimmed = (idNumber || '').trim();
  if (!trimmed) return fail('An ID number is needed to tie your OID to you.');

  const { data, error } = await supabase.rpc('fn_record_my_staff_id_number', {
    p_id_number: trimmed,
  });
  if (error) {
    if (error.code === 'PGRST202') {
      return fail('This portal needs a database update that has not been applied yet.');
    }
    return fail(error.message || 'That ID number could not be recorded.');
  }
  return { data: (Array.isArray(data) ? data[0] : data) || null, error: null };
}

/**
 * Record an admin action, and find out whether it may proceed.
 *
 * THE ORDER HERE IS THE WHOLE POINT.
 *
 * Canon 4.2 asks for a reason "before the record renders". A screen that
 * fetches the record, renders it, and then writes a log line has already shown
 * the data — the log is then a record of a disclosure that happened, not a
 * condition of it. So this is called FIRST, and the fetch happens only if it
 * returns cleanly. fn_log_my_admin_action refuses an unreasoned entry, refuses
 * an action the caller does not hold, and refuses an export with no live
 * time-boxed grant, so all three refusals land before anything is read.
 */
export async function logAdminAction({ action, reason = null, targetTable = null, targetId = null, detail = {} }) {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const { data, error } = await supabase.rpc('fn_log_my_admin_action', {
    p_action: action,
    p_reason: reason,
    p_target_table: targetTable,
    p_target_id: targetId,
    p_detail: detail,
  });
  if (error) return fail(error.message || 'That action could not be recorded, so it did not happen.');
  return { data, error: null };
}

/**
 * Do something that touches identifiable personal data.
 *
 * Wraps the pair so a caller cannot accidentally do them in the wrong order,
 * which is the only way to get this wrong and is invisible in review.
 *
 * @param {object} intent          what is being done and why
 * @param {function} run           the actual read, called ONLY if the log entry
 *                                 was accepted
 */
export async function withCapturedReason(intent, run) {
  const logged = await logAdminAction(intent);
  if (logged.error) return { data: null, error: logged.error };
  return run();
}

/** Sorted the way the database sorted them; never re-sorted here. */
export const masterCardOf = (cards) => (cards || []).find((c) => c.is_master) || null;
export const moduleCardsOf = (cards) => (cards || []).filter((c) => !c.is_master);

// ---------------------------------------------------------------------------
// HOW A PERMISSION IS WRITTEN ON A SCREEN
// ---------------------------------------------------------------------------

/**
 * Words that are not words.
 *
 * Title-casing a slug turns 'manage_api_keys' into 'Manage Api Keys', which
 * looks like a typo to anyone who knows what an API is. These stay as they are
 * written everywhere else on the platform.
 */
const ACRONYMS = {
  api: 'API', mfa: 'MFA', ofx: 'OFX', ofxb: 'OFXB', oid: 'OID',
  kyc: 'KYC', fica: 'FICA', popia: 'POPIA', prr: 'PRR', sop: 'SOP',
  pr: 'PR', sla: 'SLA', vat: 'VAT', id: 'ID', ai: 'AI', csv: 'CSV',
  cipc: 'CIPC', sars: 'SARS', ppra: 'PPRA', otp: 'OTP', url: 'URL',
};

/**
 * A permission key, as a person reads it.
 *
 *   "name the sections without underscores i.e. not approve_verification, but
 *    Approve Verification, and not flag_document_issue but Flag Document Issue"
 *   "capitalised on each first word of the permission - no underscores, i.e.
 *    not manage_agents but Manage Agents"
 *
 * The KEY is unchanged. This is a display transform and nothing else: the
 * string sent to admin_has_permission(), written to admin_activity_log and
 * compared in every SQL predicate is still the underscored slug. Renaming the
 * keys themselves would mean rewriting every permission check in the schema to
 * make a screen read better, which is the wrong direction entirely.
 *
 * `all` is the super-admin grant and is not a verb phrase, so it is named for
 * what it is rather than title-cased into the word "All".
 */
export function permissionLabel(key) {
  const raw = String(key || '').trim();
  if (!raw) return '';
  if (raw === 'all') return 'Every Permission';
  return raw
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => {
      const lower = word.toLowerCase();
      if (ACRONYMS[lower]) return ACRONYMS[lower];
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
}

/**
 * How much work is waiting behind each card, for the orange dot.
 *
 *   "if any new task or notification comes up, there's no way to tell it's
 *    there unless i manually go to the specific card I'm expecting something.
 *    we should probably add an orange dot anywhere there's a new task."
 *
 * Script 138's fn_my_portal_attention. Every count is permission-checked on its
 * own inside the database, so this asks no questions and filters nothing -- a
 * second opinion here about who may see what would be a second place for it to
 * be decided differently.
 *
 * Returns a map of card slug to count, because that is how the card grid wants
 * it. An empty map is the ordinary case and is not an error: it means there is
 * genuinely nothing waiting.
 */
export async function fetchPortalAttention() {
  if (!isSupabaseConfigured()) return { data: {}, error: null };
  const { data, error } = await supabase.rpc('fn_my_portal_attention');
  // A DOT IS NOT WORTH AN ERROR BANNER. If this call fails the portal still
  // works perfectly; the person simply has to open the cards themselves, which
  // is exactly where they were before this existed. Failing quietly here is the
  // difference between a degraded convenience and a broken portal.
  if (error) return { data: {}, error: null };
  const map = {};
  (data || []).forEach((row) => { map[row.card_slug] = row.waiting; });
  return { data: map, error: null };
}
